import logo from "@/assets/logo.png";
import { ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "June 13, 2026";

function Section({ n, title, children }) {
  return (
    <section className="mb-8" data-testid={`privacy-section-${n}`}>
      <h2 className="font-display text-xl font-bold mb-2">{n}. {title}</h2>
      <div className="space-y-2 text-sm text-black/70 leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-card/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <img src={logo} alt="Short Stack Hawai'i" className="h-9 w-auto" />
          <a href="/" data-testid="privacy-back-link" className="press inline-flex items-center gap-1.5 text-sm font-bold px-4 h-9 rounded-full border-2 border-black bg-white">
            <ArrowLeft size={15} weight="bold" /> Back
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="font-display text-3xl font-black tracking-tight mb-1">Privacy Policy</h1>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-8">Effective Date: {EFFECTIVE_DATE}</p>

        <Section n={1} title="Introduction">
          <p>Welcome to Short Stack Hawaii LLC (“Short Stack Hawaii LLC,” “we,” “our,” or “us”).</p>
          <p>Short Stack Hawaii LLC is a cloud-based business management platform designed to help businesses manage employees, scheduling, tasks, meetings, inventory, communications, and business operations using automation and artificial intelligence (AI).</p>
          <p>We value your privacy and are committed to protecting your personal information. This Privacy Policy explains what information we collect, how we use it, who we share it with, and the choices available to you.</p>
          <p>By using Short Stack Hawaii LLC, you agree to the practices described in this Privacy Policy.</p>
        </Section>

        <Section n={2} title="Information We Collect">
          <p>We collect information you provide directly and information generated through your use of the platform, including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account information:</strong> name, email address, mobile phone number, role, and password.</li>
            <li><strong>Business & employee data:</strong> schedules, shifts, availability, time-off requests, tasks, meetings, notes, announcements, and messages.</li>
            <li><strong>Communications:</strong> messages sent between employees and management within the platform.</li>
            <li><strong>Voice & content data:</strong> voice notes you record for transcription and task creation.</li>
            <li><strong>Usage & device data:</strong> log data, device/browser information, and IP address, used to operate and secure the service.</li>
          </ul>
        </Section>

        <Section n={3} title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide, operate, and maintain the platform and its features.</li>
            <li>Create and manage employee schedules, tasks, meetings, and communications.</li>
            <li>Send operational notifications by in-app alert, email, and (with your consent) SMS text message.</li>
            <li>Improve, personalize, and secure the service.</li>
            <li>Comply with legal obligations and enforce our terms.</li>
          </ul>
        </Section>

        <Section n={4} title="AI Processing">
          <p>When AI features are enabled, Short Stack Hawaii LLC may process selected information to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Organize tasks</li>
            <li>Generate meeting summaries</li>
            <li>Create schedules</li>
            <li>Recommend staffing</li>
            <li>Search business information</li>
            <li>Organize voice notes</li>
            <li>Recommend follow-up actions</li>
          </ul>
          <p>AI-generated recommendations are suggestions only.</p>
          <p>Users remain responsible for reviewing and approving AI-generated content.</p>
        </Section>

        <Section n={5} title="SMS Messaging & Mobile Data">
          <p><strong>Short Stack Hawaii LLC does not sell, rent, or share mobile phone numbers or SMS consent information with third parties or affiliates for marketing or promotional purposes. Mobile information may only be shared with service providers as necessary to deliver requested messaging services and comply with legal obligations.</strong></p>
          <p><strong>Program:</strong> Short Stack Crew SMS Notifications. SMS messages are work-related and optional. Types of messages include: published work schedules, shift changes, PTO request updates, task assignments, meeting reminders, account security notices, company announcements, and important operational alerts.</p>
          <p>Message frequency varies based on employee activity. Message and data rates may apply.</p>
          <p>You may opt out at any time by replying <strong>STOP</strong> to any message, or by disabling SMS notifications in your account settings. Reply <strong>HELP</strong> for assistance, or contact us at shortstackllc@gmail.com.</p>
          <p><strong>Consent records:</strong> When an employee enables SMS notifications, we store a consent record (including the employee identifier, mobile number, date/time and time zone, consent status and source, the consent language and version, and the applicable Privacy Policy and Terms versions) for audit and compliance purposes. Employees may withdraw consent at any time by disabling SMS notifications or replying STOP; withdrawal is recorded with its date and source.</p>
          <p>See our public <a href="/sms-consent" className="underline font-semibold">SMS Notifications program page</a> for full opt-in details.</p>
        </Section>

        <Section n={6} title="How We Share Information">
          <p>We do not sell your personal information. We may share information:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Within your business/organization, consistent with role-based access controls.</li>
            <li>With third-party service providers who process data on our behalf (see Section 10).</li>
            <li>When required by law, legal process, or to protect the rights, safety, and security of users and the platform.</li>
            <li>In connection with a business transfer, such as a merger or acquisition.</li>
          </ul>
        </Section>

        <Section n={7} title="Data Retention">
          <p>We retain personal information for as long as your account is active or as needed to provide the service, comply with legal obligations, resolve disputes, and enforce our agreements. Notification and consent records are preserved for audit purposes.</p>
        </Section>

        <Section n={8} title="Security">
          <p>We use reasonable administrative, technical, and organizational safeguards to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
        </Section>

        <Section n={9} title="Your Rights & Choices">
          <p>Depending on your location, you may have rights to access, correct, or delete your personal information, and to manage your notification preferences within the platform. To exercise these rights, contact us using the details in Section 14.</p>
        </Section>

        <Section n={10} title="Third-Party Services">
          <p>Short Stack Hawaii LLC may integrate with third-party providers including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Twilio (SMS messaging)</li>
            <li>OpenAI (AI services)</li>
            <li>Google Calendar</li>
            <li>Microsoft Outlook</li>
            <li>Payment processors</li>
            <li>Cloud storage providers</li>
          </ul>
          <p>Each provider maintains its own privacy practices.</p>
        </Section>

        <Section n={11} title="Children's Privacy">
          <p>Short Stack Hawaii LLC is intended for businesses and users who are at least 18 years old or the age of majority required by applicable law.</p>
          <p>We do not knowingly collect information from children.</p>
        </Section>

        <Section n={12} title="International Data Transfers">
          <p>If Short Stack Hawaii LLC processes information outside your country, we will use reasonable safeguards appropriate for those transfers where required by law.</p>
        </Section>

        <Section n={13} title="Changes to This Privacy Policy">
          <p>We may update this Privacy Policy periodically.</p>
          <p>When material changes occur, we will:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Update the Effective Date</li>
            <li>Notify users through the application when appropriate</li>
            <li>Request renewed acceptance if required</li>
          </ul>
          <p>Continued use of Short Stack Hawaii LLC after changes become effective constitutes acceptance of the updated Privacy Policy where permitted by law.</p>
        </Section>

        <Section n={14} title="Contact Us">
          <p>If you have questions regarding this Privacy Policy, please contact:</p>
          <p><strong>Short Stack Hawaii LLC</strong><br />
            Email: <a className="underline" href="mailto:shortstackllc@gmail.com">shortstackllc@gmail.com</a><br />
            Website: <a className="underline" href="https://www.shortstackhawaiicrew.com">https://www.shortstackhawaiicrew.com</a></p>
        </Section>

        <Section n={15} title="Your Consent">
          <p>By creating an account or using Short Stack Hawaii LLC, you acknowledge that you have read and understood this Privacy Policy and consent to the collection, use, and disclosure of your information as described in this Privacy Policy.</p>
        </Section>

        <div className="pt-6 border-t border-border text-sm">
          <a href="/terms" className="underline font-bold" data-testid="privacy-to-terms">View our Terms &amp; Conditions →</a>
        </div>
      </main>
    </div>
  );
}
